/**
 * WebSocket Manager for Cipher real-time features.
 * Handles price feeds, alert notifications, and market status broadcasts.
 */

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

export interface PriceUpdate {
  type: 'price_update'
  ticker: string
  price: number
  change: number
  change_pct: number
  volume: number
  bid?: number
  ask?: number
  timestamp: string
}

export interface ScoreUpdate {
  type: 'score_update'
  ticker: string
  new_score: number
  old_score: number
  delta: number
  direction: 'up' | 'down'
  timestamp: string
}

export interface AlertFired {
  type: 'alert_fired'
  user_id: string
  alert_id: string
  event_id: string
  ticker: string | null
  severity: 'critical' | 'high' | 'medium' | 'low'
  title: string
  body: string
  event_type: string
  fired_at: string
}

export interface MarketStatus {
  type: 'market_status'
  is_open: boolean
  regime: string
  spy_change_pct: number
  spy_price: number
  vix: number
  vix_change_pct: number
  timestamp: string
}

export interface MarketEvent {
  type: 'market_open' | 'market_close'
  summary?: string
  timestamp: string
}

export type WebSocketMessage = PriceUpdate | ScoreUpdate | AlertFired | MarketStatus | MarketEvent | { type: string; [key: string]: unknown }

interface WebSocketConnection {
  ws: WebSocket | null
  status: ConnectionStatus
  retryCount: number
  retryTimeout: ReturnType<typeof setTimeout> | null
  heartbeatInterval: ReturnType<typeof setInterval> | null
  messageHandler: ((data: WebSocketMessage) => void) | null
}

// Faster reconnection with more retries
const RETRY_DELAYS = [500, 1000, 2000, 5000, 10000] // Fast initial retries
const MAX_RETRIES = 10
const HEARTBEAT_INTERVAL = 25000 // Send heartbeat every 25 seconds

// Derive WebSocket URL from API URL with proper fallback
function getDefaultWsUrl(): string {
  // First try VITE_WS_URL if explicitly set
  const wsUrl = import.meta.env.VITE_WS_URL
  if (wsUrl && wsUrl !== 'undefined') {
    return wsUrl
  }
  // Otherwise derive from VITE_API_URL
  const apiUrl = import.meta.env.VITE_API_URL || 'https://api.cipherequity.com'
  return apiUrl.replace('https://', 'wss://').replace('http://', 'ws://')
}

export class ApexWebSocketManager {
  private baseUrl: string
  private connections: Map<string, WebSocketConnection> = new Map()
  private statusListeners: Set<(channel: string, status: ConnectionStatus) => void> = new Set()

  constructor(baseUrl?: string) {
    // Use provided baseUrl, or derive from environment with production fallback
    this.baseUrl = baseUrl || getDefaultWsUrl()
  }

  /**
   * Add a listener for connection status changes
   */
  onStatusChange(listener: (channel: string, status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(listener)
    return () => this.statusListeners.delete(listener)
  }

  private notifyStatusChange(channel: string, status: ConnectionStatus): void {
    this.statusListeners.forEach(listener => listener(channel, status))
  }

  private getConnection(channel: string): WebSocketConnection {
    if (!this.connections.has(channel)) {
      this.connections.set(channel, {
        ws: null,
        status: 'disconnected',
        retryCount: 0,
        retryTimeout: null,
        heartbeatInterval: null,
        messageHandler: null
      })
    }
    return this.connections.get(channel)!
  }

  private startHeartbeat(channel: string): void {
    const conn = this.getConnection(channel)
    // Clear existing heartbeat
    if (conn.heartbeatInterval) {
      clearInterval(conn.heartbeatInterval)
    }
    // Start new heartbeat
    conn.heartbeatInterval = setInterval(() => {
      if (conn.ws && conn.status === 'connected') {
        try {
          conn.ws.send(JSON.stringify({ type: 'ping' }))
        } catch (e) {
          console.warn(`[WS] ${channel} heartbeat failed:`, e)
        }
      }
    }, HEARTBEAT_INTERVAL)
  }

  private stopHeartbeat(channel: string): void {
    const conn = this.getConnection(channel)
    if (conn.heartbeatInterval) {
      clearInterval(conn.heartbeatInterval)
      conn.heartbeatInterval = null
    }
  }

  private updateStatus(channel: string, status: ConnectionStatus): void {
    const conn = this.getConnection(channel)
    conn.status = status
    this.notifyStatusChange(channel, status)
  }

  private connect(
    channel: string,
    path: string,
    onMessage: (data: WebSocketMessage) => void,
    queryParams?: Record<string, string>
  ): void {
    const conn = this.getConnection(channel)

    // Clear any existing retry timeout
    if (conn.retryTimeout) {
      clearTimeout(conn.retryTimeout)
      conn.retryTimeout = null
    }

    // Close existing connection if any
    if (conn.ws) {
      conn.ws.close()
    }

    conn.messageHandler = onMessage
    this.updateStatus(channel, 'connecting')

    // Build URL with query params
    let url = `${this.baseUrl}${path}`
    if (queryParams) {
      const params = new URLSearchParams(queryParams)
      url += `?${params.toString()}`
    }

    try {
      const ws = new WebSocket(url)
      conn.ws = ws

      ws.onopen = () => {
        console.log(`[WS] ${channel} connected`)
        conn.retryCount = 0
        this.updateStatus(channel, 'connected')
        // Start heartbeat to keep connection alive
        this.startHeartbeat(channel)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (conn.messageHandler) {
            conn.messageHandler(data)
          }
        } catch (e) {
          console.error(`[WS] ${channel} parse error:`, e)
        }
      }

      ws.onerror = (error) => {
        console.error(`[WS] ${channel} error:`, error)
        this.updateStatus(channel, 'error')
      }

      ws.onclose = (event) => {
        console.log(`[WS] ${channel} closed:`, event.code, event.reason)
        this.stopHeartbeat(channel)
        this.updateStatus(channel, 'disconnected')

        // Auto-reconnect if not intentionally closed or if connection was lost
        if ((event.code !== 1000 || event.wasClean === false) && conn.retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAYS[Math.min(conn.retryCount, RETRY_DELAYS.length - 1)]
          conn.retryCount++
          console.log(`[WS] ${channel} reconnecting in ${delay}ms (attempt ${conn.retryCount}/${MAX_RETRIES})`)

          conn.retryTimeout = setTimeout(() => {
            if (conn.messageHandler) {
              this.connect(channel, path, conn.messageHandler, queryParams)
            }
          }, delay)
        } else if (conn.retryCount >= MAX_RETRIES) {
          console.error(`[WS] ${channel} max retries (${MAX_RETRIES}) reached, giving up`)
        }
      }
    } catch (error) {
      console.error(`[WS] ${channel} connection error:`, error)
      this.updateStatus(channel, 'error')
    }
  }

  /**
   * Connect to the price feed
   */
  connectPrices(
    tickers: string[],
    onMessage: (data: PriceUpdate | ScoreUpdate) => void,
    token?: string
  ): void {
    const params: Record<string, string> = {}
    if (tickers.length > 0) {
      params.tickers = tickers.join(',')
    }
    if (token) {
      params.token = token
    }
    this.connect('prices', '/ws/prices', onMessage as (data: WebSocketMessage) => void, params)
  }

  /**
   * Connect to the alert feed
   */
  connectAlerts(
    userId: string,
    token: string,
    onMessage: (data: AlertFired) => void
  ): void {
    this.connect(
      'alerts',
      `/ws/alerts/${userId}`,
      onMessage as (data: WebSocketMessage) => void,
      { token }
    )
  }

  /**
   * Connect to the market status feed
   */
  connectMarket(onMessage: (data: MarketStatus | MarketEvent) => void): void {
    this.connect('market', '/ws/market', onMessage as (data: WebSocketMessage) => void)
  }

  /**
   * Subscribe to additional tickers (prices channel only)
   */
  subscribeTickers(tickers: string[]): void {
    const conn = this.getConnection('prices')
    if (conn.ws && conn.status === 'connected') {
      conn.ws.send(JSON.stringify({
        type: 'subscribe',
        tickers: tickers.map(t => t.toUpperCase())
      }))
    }
  }

  /**
   * Unsubscribe from tickers (prices channel only)
   */
  unsubscribeTickers(tickers: string[]): void {
    const conn = this.getConnection('prices')
    if (conn.ws && conn.status === 'connected') {
      conn.ws.send(JSON.stringify({
        type: 'unsubscribe',
        tickers: tickers.map(t => t.toUpperCase())
      }))
    }
  }

  /**
   * Send a ping to keep connection alive
   */
  ping(channel: string): void {
    const conn = this.getConnection(channel)
    if (conn.ws && conn.status === 'connected') {
      conn.ws.send(JSON.stringify({ type: 'ping' }))
    }
  }

  /**
   * Mark an alert as read
   */
  markAlertRead(alertId: string): void {
    const conn = this.getConnection('alerts')
    if (conn.ws && conn.status === 'connected') {
      conn.ws.send(JSON.stringify({
        type: 'mark_read',
        alert_id: alertId
      }))
    }
  }

  /**
   * Get status of a specific channel
   */
  getStatus(channel: string): ConnectionStatus {
    return this.getConnection(channel).status
  }

  /**
   * Get status of all channels
   */
  getAllStatuses(): Record<string, ConnectionStatus> {
    const statuses: Record<string, ConnectionStatus> = {}
    this.connections.forEach((conn, channel) => {
      statuses[channel] = conn.status
    })
    return statuses
  }

  /**
   * Disconnect a specific channel
   */
  disconnectChannel(channel: string): void {
    const conn = this.getConnection(channel)

    // Stop heartbeat
    this.stopHeartbeat(channel)

    if (conn.retryTimeout) {
      clearTimeout(conn.retryTimeout)
      conn.retryTimeout = null
    }

    if (conn.ws) {
      conn.ws.close(1000, 'Client disconnect')
      conn.ws = null
    }

    conn.messageHandler = null
    conn.retryCount = 0
    this.updateStatus(channel, 'disconnected')
  }

  /**
   * Disconnect all channels
   */
  disconnect(): void {
    this.connections.forEach((_, channel) => {
      this.disconnectChannel(channel)
    })
    this.connections.clear()
  }
}

// Global WebSocket manager instance
let wsManager: ApexWebSocketManager | null = null

export function getWebSocketManager(): ApexWebSocketManager {
  if (!wsManager) {
    wsManager = new ApexWebSocketManager()
  }
  return wsManager
}

export function resetWebSocketManager(): void {
  if (wsManager) {
    wsManager.disconnect()
    wsManager = null
  }
}
